import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Loader2, ShieldAlert } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell,
} from 'recharts';
import { api, type AperturasManuales } from '@/lib/api';
import {
  useChartTheme, madridDay, monthLabel, Panel, Stat, HeatmapSemanal,
} from '@/components/aperturas/shared';
import type { AperturasContext } from './AperturasLayout';

export default function AperturasManualesPage() {
  const chart = useChartTheme();
  const { days } = useOutletContext<AperturasContext>();
  const [data, setData] = useState<AperturasManuales | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api.getAperturasManuales(madridDay(days - 1), madridDay())
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Error cargando datos'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [days]);

  const totalNoAtribuible = data?.serieMeses.reduce((s, m) => s + m.noAtribuibles, 0) ?? 0;
  const total = data?.serieMeses.reduce((s, m) => s + m.total, 0) ?? 0;

  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500 -mt-1">Aperturas que Nuki no puede atribuir a nadie: el pomo, la llave y el botón del dispositivo no llevan usuario.</p>

      {loading && (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="animate-spin mr-2" size={20} /> Cargando…
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-500/10 dark:border-red-500/30 p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {data && !loading && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Stat
              label="Aperturas sin trazabilidad"
              value={total ? `${((100 * totalNoAtribuible) / total).toFixed(1)}%` : '—'}
              hint={`${totalNoAtribuible.toLocaleString('es-ES')} de ${total.toLocaleString('es-ES')}`}
            />
            <Stat label="Códigos rechazados" value={data.rechazos.total.toLocaleString('es-ES')} hint="teclado con código no válido" />
            <Stat label="Apartamentos con más de un 40% manual" value={data.ranking.filter((d) => d.pctNoAtribuible >= 40).length} />
          </div>

          <Panel
            title="Evolución del porcentaje no atribuible"
            hint="Es una medida de cobertura del teclado, no de intrusión: un huésped saliendo de casa por el pomo interior cuenta aquí igual que una llave física de un tercero."
          >
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.serieMeses} margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 11, fill: chart.axis }} />
                <YAxis yAxisId="l" tick={{ fontSize: 12, fill: chart.axis }} allowDecimals={false} />
                <YAxis yAxisId="r" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12, fill: chart.axis }} />
                <Tooltip cursor={{ fill: chart.cursor }} labelFormatter={(m: any) => monthLabel(String(m))} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="l" dataKey="noAtribuibles" name="No atribuibles" stackId="a" fill="#B45309" radius={[0, 0, 0, 0]} />
                <Bar yAxisId="l" dataKey="keypad" name="Teclado" stackId="a" fill="#3B6EA5" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <Panel
            title="Reloj semanal de aperturas no atribuibles"
            hint="Actividad de madrugada sin usuario asociado es lo que más merece una mirada."
          >
            <HeatmapSemanal cells={data.heatmap} unidad="aperturas manuales" />
          </Panel>

          <Panel
            title="Ranking por apartamento"
            hint="Solo apartamentos con 10 o más aperturas en el rango: por debajo de eso, un solo evento mueve el porcentaje demasiado."
          >
            <ResponsiveContainer width="100%" height={Math.max(220, data.ranking.slice(0, 20).length * 26)}>
              <BarChart data={data.ranking.slice(0, 20)} layout="vertical" margin={{ top: 4, right: 40, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12, fill: chart.axis }} />
                <YAxis type="category" dataKey="deviceLabel" width={185} tick={{ fontSize: 11, fill: chart.axis }} />
                <Tooltip
                  cursor={{ fill: chart.cursor }}
                  formatter={(v: any, _k: any, p: any) =>
                    [`${v}% · ${p.payload.noAtribuibles} de ${p.payload.total} aperturas`, 'Sin trazabilidad']}
                />
                <Bar dataKey="pctNoAtribuible" radius={[0, 3, 3, 0]}>
                  {data.ranking.slice(0, 20).map((d) => (
                    <Cell key={d.smartlockId} fill={d.pctNoAtribuible >= 40 ? '#B45309' : chart.palette[1]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Panel
              title="Códigos rechazados por hora"
              hint="Uno o dos seguidos es un huésped equivocándose. Una ráfaga a la misma hora, no."
            >
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.rechazos.porHora} margin={{ top: 4, right: 8, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="hour" tickFormatter={(h) => `${h}h`} tick={{ fontSize: 11, fill: chart.axis }} />
                  <YAxis tick={{ fontSize: 12, fill: chart.axis }} allowDecimals={false} />
                  <Tooltip cursor={{ fill: chart.cursor }} labelFormatter={(h) => `${h}:00 – ${h}:59`} formatter={(v: any) => [`${v} rechazos`, '']} />
                  <Bar dataKey="rechazos" fill="#B45309" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>

            <Panel title="Rechazos por apartamento">
              <div className="max-h-[200px] overflow-y-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {data.rechazos.porDispositivo.slice(0, 12).map((d) => (
                      <tr key={d.smartlockId} className="border-t border-slate-100 dark:border-white/5 first:border-0">
                        <td className="py-1.5 text-sereno-dark">{d.deviceLabel}</td>
                        <td className="py-1.5 text-right tabular-nums font-medium">{d.n}</td>
                      </tr>
                    ))}
                    {data.rechazos.porDispositivo.length === 0 && (
                      <tr><td className="py-2 text-slate-400">Sin rechazos en el rango.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-white/10 p-4 flex gap-3 text-sm text-slate-500">
            <ShieldAlert size={18} className="text-slate-400 shrink-0 mt-0.5" />
            <p>
              Ninguna de estas gráficas identifica a quien abrió. Nuki no manda usuario en los eventos físicos, y ningún
              cruce con reservas lo arregla. Sirven para localizar apartamentos donde el teclado se usa poco, no para
              atribuir responsabilidad.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
